import { Dispatch, FC, SetStateAction, useRef, useState } from 'react'
import { StackLayout } from '@progress/kendo-react-layout'
import { Window } from '@progress/kendo-react-dialogs'
import { DateSetting, DateSettingHandler } from './DateSetting'
import { Button } from '@progress/kendo-react-buttons'
import { ConfirmDialog, ErrorMessage } from '../../components/parts'
import { PutDateGateway } from '../../gateway/home/PutDateGateway'
import { useAppSelector } from '../../stores'
import { Authority } from '../../gateway/system/Authority'

export interface DateSettingInputProps {
  // 日程の編集画面を閉じる
  onCloseEditWindow: () => void
  // 登録修正完了、トップ画面データを再リクエストする
  refetchHomeInformation: () => void
  // エラーメッセージを設定する
  setErrorMessages: Dispatch<SetStateAction<ErrorMessage[]>>
}

export const DateSettingInput: FC<DateSettingInputProps> = (props) => {
  /* Dialog info */
  const [confirmDialogShow, setConfirmDialogShow] = useState(false)
  const [title, setTitle] = useState('')
  const [args, setArgs] = useState([''])
  const [dialogMsgId, setDialogMsgId] = useState('I003')
  const [renderCancelButton, setRenderCancelButton] = useState(true)

  const DateSettingRef = useRef<DateSettingHandler>(null)

  const userProp = useAppSelector((x) => x.common.userProp)

  /**
   * 登録ボタン押下
   */
  const handleClickLoginBtn = () => {
    setConfirmDialogShow(true)
    setTitle('登録')

    const updatedDate = DateSettingRef.current?.getUpdatedDate()
    if (updatedDate !== undefined && checkValidDateString(updatedDate.date)) {
      setArgs(['登録'])
      setDialogMsgId('I007')
    } else {
      // 有効な日付が入力されていない場合
      setArgs(['有効な日付'])
      setDialogMsgId('E002')
      setRenderCancelButton(false)
    }
  }
  /**
   * 戻るボタン押下
   */
  const handleReturn = () => {
    setConfirmDialogShow(true)
    setTitle('戻る')
    // TODO 差分チェックは行っておらず、必ず破棄の確認が出る状態になっている
    setDialogMsgId('I003')
  }
  /**
   * Dialog 確認ボタン
   */
  const handleConfirmEvent = () => {
    if (title === '登録') {
      const updatedDate = DateSettingRef.current?.getUpdatedDate()

      if (updatedDate !== undefined && checkValidDateString(updatedDate.date)) {
        PutDateGateway.putDate(updatedDate)
          .then((res) => {
            if (!res.error) {
              // 登録修正完了
              setTitle('登録完了')
              setDialogMsgId('I004')
              setRenderCancelButton(false)
            } else {
              if (Array.isArray(res.messages) && res.messages.length > 0) {
                const errorMessages = res.messages.map<ErrorMessage>((message) => {
                  return {
                    id: message.id,
                    content: message.message
                  }
                })
                props.setErrorMessages(errorMessages)
              }
            }
          })
          .finally(() => {})
      } else {
        // 有効な日付が入力されていない場合
        setArgs([''])
        setConfirmDialogShow(false)
        setRenderCancelButton(true)
      }
    }

    if (title === '戻る') {
      setConfirmDialogShow(false)
      setArgs([''])
      setDialogMsgId('I003')
      props.onCloseEditWindow()
      // トップ画面データを再リクエストする
      props.refetchHomeInformation()
    }

    if (title === '登録完了') {
      setArgs([''])
      setConfirmDialogShow(false)
      setRenderCancelButton(true)
      // 日付情報を再取得
      DateSettingRef.current?.fetchDate()
    }
  }

  /**
   * 日付文字列が有効な日付かをチェックする
   * @param strDate 日付文字列
   * @returns true: 有効な日付、false: 無効な日付
   */
  const checkValidDateString = (strDate: string) => {
    if (strDate === undefined) {
      return false
    }

    const year = Number(strDate.substring(0, 4))
    const month = Number(strDate.substring(5, 7))
    const day = Number(strDate.substring(8, 10))
    const date = new Date(year, month - 1, day)

    return month === date.getMonth() + 1
  }

  return (
    <>
      <Window
        title={userProp?.authority === Authority.Builder ? '日程の編集' : '日程の参照（編集不可）'}
        onClose={props.onCloseEditWindow}
        initialWidth={1200}
        initialHeight={280}
        modal
      >
        <DateSetting ref={DateSettingRef} />

        <StackLayout
          gap={5}
          align={{ horizontal: 'end' }}
          style={{ marginTop: 10 }}
        >
          <Button
            onClick={handleReturn}
            className="pcy-button-short"
          >
            戻る
          </Button>

          <Button
            onClick={handleClickLoginBtn}
            themeColor="primary"
            className="pcy-button-short"
            disabled={userProp?.authority !== Authority.Builder}
          >
            登録
          </Button>
        </StackLayout>
      </Window>

      {confirmDialogShow && (
        <ConfirmDialog
          open={confirmDialogShow}
          title={title}
          id={dialogMsgId}
          renderCancelButton={renderCancelButton}
          args={args}
          onClickOk={handleConfirmEvent}
          onClickCancel={() => setConfirmDialogShow(false)}
        />
      )}
    </>
  )
}
